import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Video, Car, Briefcase } from 'lucide-react';
import CTA from '../../components/CTA';

const LimavadyHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Limavady | Professional Support for North West Market Town</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Limavady market town. Online sessions recommended for convenience. In-person available at Belfast clinic on the Ormeau Road. Business stress, anxiety, work-life balance. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Limavady, hypnotherapist Limavady, online therapy Limavady, anxiety treatment Limavady, business stress North West, market town therapy, work stress Limavady, Roe Valley hypnotherapy" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/limavady-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Limavady | North West Market Town Support" />
        <meta property="og:description" content="Professional hypnotherapy for Limavady market town. Online sessions recommended. In-person at Belfast clinic on the Ormeau Road. Business stress, anxiety, flexible scheduling." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Limavady",
            "description": "CNHC registered hypnotherapy serving Limavady and North West region. Specializing in business stress, anxiety management, work-life balance, entrepreneurial pressure, food industry support, and accessible mental health care for market town professionals.",
            "url": "https://www.sharonclare-hypnotherapy.com/limavady-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Limavady",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Londonderry"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Online Hypnotherapy",
              "Business Stress Management",
              "Anxiety Treatment",
              "Work-Life Balance",
              "Entrepreneurial Support",
              "Food Industry Stress"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Limavady
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Flexible support for North West market town professionals and residents
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-6 border-2 border-accent-500">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <Video className="w-8 h-8 text-accent-600" />
                  <p className="text-lg font-semibold text-gray-900">
                    Online Sessions
                  </p>
                </div>
                <p className="text-gray-700 text-sm">
                  Secure video call from home — no travel, with flexible times throughout the week
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-primary-300">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <MapPin className="w-8 h-8 text-primary-600" />
                  <p className="text-lg font-semibold text-gray-900">
                    Belfast & Lisburn Clinics
                  </p>
                </div>
                <p className="text-gray-700 text-sm">
                  Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ, or The Arzen Collective, 13B Antrim Street, Lisburn. Clinic days and times vary - early evening appointments are available.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Limavady's Thriving Market Town Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I'm delighted to serve Limavady's dynamic market town community with professional hypnotherapy. Limavady, with a population of approximately 11,000 people, is a thriving hub in the North West - known for its independent retailers, award-winning food and drink businesses, cultural heritage (the "Danny Boy" connection), and the scenic Roe Valley. Located 17 miles east of Derry and 14 miles southwest of Coleraine, Limavady has maintained its market town character while growing into a center for food innovation and entrepreneurial activity.
              </p>
              <p>
                As a market town professional - whether you're running an independent business on the pedestrian shopping zone, working in the food and drink sector, managing a retail operation, or juggling demanding work schedules - time is valuable. For Limavady residents, <strong>online sessions</strong> are usually the most practical option: available throughout the week, they eliminate travel entirely and make weekly appointments genuinely sustainable without disrupting your workday. I also hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, and at The Arzen Collective, 13B Antrim Street, Lisburn, with days and times that vary week to week. All options provide the same professional CNHC-registered support.
              </p>
              <p>
                Whether you're experiencing business stress from scaling a food enterprise, managing the pressures of independent retail in a competitive market, dealing with work-life balance challenges, navigating generalized anxiety or sleep problems, or addressing chronic pain or unwanted habits, hypnotherapy offers evidence-based support that respects your time and priorities. Online sessions provide maximum efficiency and flexibility, and for those who prefer face-to-face connection, in-person sessions are available at the Belfast clinic on the Ormeau Road or the Lisburn clinic on Antrim Street. Both options provide the same quality of care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Online Sessions Work for Limavady */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Why Many Limavady Clients Choose Online Sessions
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      No Travel Required
                    </h3>
                    <p className="text-gray-700">
                      At approximately 60 miles from Limavady, the Belfast clinic is a significant journey for regular appointments. Online sessions eliminate travel entirely - professional hypnotherapy from your home without any commute. For busy market town professionals, business owners, and working parents, this makes consistent weekly therapy realistic without disrupting your workday or family schedule.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Same Effectiveness as In-Person
                    </h3>
                    <p className="text-gray-700">
                      Research shows online hypnotherapy is equally effective as face-to-face sessions. Hypnosis works through verbal guidance and accessing your subconscious mind - physical proximity isn't necessary. You get the same professional CNHC-registered therapy, just delivered conveniently without travel. No compromise on quality, just better efficiency.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Briefcase className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Perfect for Business Schedules
                    </h3>
                    <p className="text-gray-700">
                      Running an independent business? Managing a food production operation? Working retail hours? Online sessions provide scheduling flexibility that works with entrepreneurial realities. Book appointments during lunchtime, between shifts, during school hours, or evenings - no need to close your shop or leave your business for extended periods to travel.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Video className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Flexible Access from Anywhere
                    </h3>
                    <p className="text-gray-700">
                      Whether you're at home in Limavady town center, working from a rural property in the Roe Valley area, or managing business commitments that take you around the North West, online sessions provide consistent access to therapy. Travel for work doesn't interrupt your mental health support - you can maintain appointments regardless of location.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-accent-50 rounded-xl p-6 border-l-4 border-accent-600">
              <p className="text-gray-800">
                <strong>Convenience meets effectiveness:</strong> For Limavady residents, online sessions are the most practical option given the distance to our Belfast clinic on the Ormeau Road. Online sessions provide the exact same quality therapy from your home or office, with no travel time at all. Many clients appreciate the ability to fit sessions into lunch breaks or between commitments. If you do prefer face-to-face sessions, the Belfast clinic on the Ormeau Road in south Belfast is there when it suits you - clinic days and times vary, and early evening appointments are available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Limavady Community */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Services Tailored to Limavady's Market Town Community
            </h2>
            <p className="text-lg text-gray-700 mb-12">
              Limavady's thriving market town economy - independent businesses, award-winning food sector, retail operations, cultural tourism - brings particular stressors. Business scaling pressures, work-life balance challenges, entrepreneurial stress, economic uncertainty, and the demands of maintaining quality in competitive markets all take a toll on mental health. Hypnotherapy provides evidence-based support for the specific pressures facing North West professionals and residents.
            </p>

            <div className="space-y-10">
              {/* Business and Entrepreneurial Stress */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Business Stress and Entrepreneurial Pressure
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Limavady's economy is characterized by independent, family-owned businesses - boutique retailers on the pedestrian shopping zone, food and drink enterprises like Corndale Farm Charcuterie and Broighter Gold Rapeseed Oils, market traders at the Roe Valley Speciality Market, hospitality businesses serving heritage tourism, and professionals at the North West Regional College's Foodovation Centre. This entrepreneurial character brings vitality and local pride, but also significant stress.
                  </p>
                  <p>
                    Business owners and entrepreneurs face unique pressures: financial uncertainty from cash flow challenges and market volatility, the weight of responsibility for employees and family livelihoods, difficulty separating work and personal life when your business is your identity, perfectionism and fear of failure in competitive markets, isolation from bearing problems alone without traditional workplace support structures, and the constant pressure to scale, innovate, and maintain quality. For food sector businesses, there are additional stressors around food safety regulations, seasonal demand, production challenges, and the high-stakes nature of the industry.
                  </p>
                  <p>
                    Hypnotherapy provides targeted support for business and entrepreneurial stress: managing chronic anxiety about finances and business viability, improving sleep quality when work worries keep you awake at night, building resilience for the inevitable ups and downs of self-employment, reducing perfectionism and catastrophic thinking patterns ("one mistake will ruin everything"), establishing mental boundaries between work and personal life, processing imposter syndrome ("who am I to run a business?"), and developing stress management strategies that acknowledge the real pressures without letting them overwhelm you. Online sessions are particularly practical for business owners - you can schedule appointments without leaving your operation for extended periods, and the time efficiency supports consistent therapy even during busy trading periods.
                  </p>
                </div>
              </div>

              {/* Work-Life Balance and Burnout */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Work-Life Balance and Burnout Prevention
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Market town living in Limavady brings benefits - community connection, shorter commutes than cities, access to the Roe Valley greenway and rural surroundings - but doesn't automatically solve work-life balance challenges. Many residents work demanding jobs (retail, food production, healthcare, education), commute to Derry or Coleraine, run family businesses with irregular hours, or juggle multiple responsibilities. The result: chronic stress, exhaustion, feeling constantly "on," and the gradual erosion of time for rest, relationships, and activities that bring joy.
                  </p>
                  <p>
                    Burnout is the eventual outcome of sustained work-life imbalance - emotional exhaustion where you have nothing left to give, cynicism and detachment from work that once mattered to you, reduced performance despite working harder than ever, physical symptoms like headaches and digestive problems, and the sense of being trapped in a life that's all obligation and no fulfillment. Many high-functioning people burn out slowly without recognizing what's happening until they're in crisis.
                  </p>
                  <p>
                    Hypnotherapy addresses work-life balance and burnout through both symptom management and deeper pattern work. Immediate techniques include: stress reduction and nervous system regulation to manage chronic overwhelm, sleep optimization to address burnout-related insomnia, boundary-setting support to establish realistic limits on work demands, and cognitive reframing to challenge beliefs like "saying no means I'm failing" or "rest is selfish." Deeper work explores: identifying your actual values versus inherited expectations, processing guilt around prioritizing self-care, building authentic self-worth that isn't dependent on productivity, and creating sustainable rhythms that honor both ambition and human limitations. For Limavady's driven professionals and business owners, this isn't about working less (though that might be appropriate) - it's about working in ways that are genuinely sustainable rather than slowly destructive.
                  </p>
                </div>
              </div>

              {/* Food Industry Specific Support */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Food Industry Stress and High-Pressure Sectors
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Limavady has emerged as a significant hub for food and drink businesses, with the North West Regional College's Foodovation and Skills Centre, multi-award-winning enterprises, and a growing reputation for quality food production. This brings economic opportunity and pride, but also high-pressure working conditions. The food industry is notoriously stressful: long hours on production lines or in commercial kitchens, physically demanding work, high-stakes food safety responsibilities where mistakes can have serious consequences, temperature extremes and challenging working environments, seasonal production pressures, and economic volatility from ingredient costs and market competition.
                  </p>
                  <p>
                    Food sector workers and business owners often experience: chronic anxiety about food safety compliance and potential incidents, perfectionism driven by the high standards required in the industry, physical exhaustion from long shifts and repetitive work, stress from scaling production while maintaining quality, and the mental load of managing every detail in a field where attention to detail is literally critical. For those at the innovation end (Foodovation Centre, product development), there's additional pressure around creativity, R&D timelines, competition, and bringing new products to market successfully.
                  </p>
                  <p>
                    Hypnotherapy helps food industry professionals manage this sector-specific stress: reducing anxiety while maintaining appropriate attention to safety (anxiety that enhances performance versus anxiety that impairs it), improving sleep quality after long shifts, managing perfectionism so it drives excellence without causing paralysis, building resilience for production pressures and seasonal demands, processing workplace stress and difficult dynamics, and establishing mental boundaries between work stress and home life. The food industry won't become less demanding, but your nervous system's response to those demands can become more regulated and sustainable. Online sessions are particularly convenient for shift workers and those with irregular food production hours.
                  </p>
                </div>
              </div>

              {/* General Anxiety and Stress Management */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Anxiety, Stress, and Sleep Problems
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Beyond Limavady-specific stressors, many residents experience generalized anxiety - persistent worry, racing thoughts, physical tension, sleep disturbances, panic attacks, and the overwhelming feeling that something bad is always about to happen. This might be triggered by work pressures, family dynamics, health concerns, financial stress, or nothing obvious at all - just a constant background hum of unease that colors everything. Sleep problems often accompany anxiety: difficulty falling asleep due to racing thoughts, waking at 3-4am with worry, or sleeping poorly and waking exhausted despite being in bed 8 hours.
                  </p>
                  <p>
                    Hypnotherapy addresses anxiety and sleep at the subconscious level where automatic patterns are stored. Instead of just managing symptoms consciously (helpful but incomplete), clinical hypnosis helps retrain the nervous system's threat response. Techniques include: progressive relaxation to release chronic physical tension, cognitive reframing to challenge catastrophic thinking patterns, visualization to create a felt sense of safety, sleep-specific hypnosis to quiet mental chatter at bedtime, anchoring techniques to access calm states during anxious moments, and subconscious suggestion to reduce the baseline anxiety level that affects your entire day.
                  </p>
                  <p>
                    Research evidence supports hypnotherapy for anxiety and sleep disorders, with some studies finding it as effective as CBT. Many clients report significant improvement within 4-6 sessions - anxiety becomes manageable rather than overwhelming, sleep quality improves, physical tension reduces. For Limavady residents, the ability to access this evidence-based treatment efficiently (online) or with an occasional trip to the Belfast clinic makes consistent therapy achievable without major time commitments or travel to larger cities for specialist services.
                  </p>
                </div>
              </div>

              {/* Additional Services */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Smoking Cessation and Habit Change
                  </h4>
                  <p className="text-gray-700">
                    Ready to quit smoking? Break stress-eating patterns? Address nail-biting or other unwanted habits developed as coping mechanisms? Hypnotherapy targets the subconscious drivers of automatic behaviors - more effective than willpower alone. Online sessions provide convenient access without travel time.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Chronic Pain Management
                  </h4>
                  <p className="text-gray-700">
                    Living with fibromyalgia, back pain from physical work, migraines, arthritis, or other persistent pain? Hypnotherapy helps reduce pain intensity and improve quality of life by working with your nervous system's pain processing. NHS-complementary support, accessible from Limavady.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Confidence and Performance
                  </h4>
                  <p className="text-gray-700">
                    Imposter syndrome affecting your business? Public speaking anxiety for presentations? Social anxiety in market town networking contexts? Hypnotherapy builds genuine confidence from within - authentic self-acceptance and capability recognition rather than fake-it-till-you-make-it pretense.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Phobias and Specific Fears
                  </h4>
                  <p className="text-gray-700">
                    Dental phobia, driving anxiety, fear of medical procedures, flying phobia affecting business travel? Hypnotherapy effectively treats specific phobias, often in just 2-4 sessions. Address fears that limit your life and work without lengthy therapy commitments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There / Access Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Accessing Therapy from Limavady
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-xl p-8 border-2 border-accent-500">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                  <Video className="w-6 h-6 text-accent-600 mr-3" />
                  Online Sessions (Recommended)
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="font-semibold text-lg">Maximum efficiency for busy professionals - preferred by many Limavady clients</p>
                  <p>
                    <strong>How it works:</strong> Sessions conducted via secure video platform (Zoom or similar). You need a private space, reliable internet connection, and a device with camera/microphone (phone, tablet, laptop, or desktop computer all work).
                  </p>
                  <p><strong>Effectiveness:</strong> Research confirms online hypnotherapy is equally effective as in-person. Hypnosis works through verbal guidance and subconscious processes - physical proximity isn't necessary. Same professional CNHC-registered therapy, just delivered conveniently.</p>
                  <p><strong>Time efficiency:</strong> Eliminates over 2 hours of travel per session that attending the Belfast clinic would require. For weekly appointments, this saves significant time - essential for busy business owners, working professionals, and parents juggling commitments.</p>
                  <p><strong>Scheduling flexibility:</strong> Book appointments during lunch breaks, between shifts, after hours, weekends - whatever works with your market town schedule. No need to close your business or take extended time off work for travel.</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 border-2 border-primary-300">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                  <Car className="w-6 h-6 text-primary-600 mr-3" />
                  Belfast Clinic (In-Person Option)
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="font-semibold">A long journey from Limavady - online sessions recommended</p>
                  <p>
                    <strong>Getting here:</strong> The Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ, on the Ormeau Road (A24) in south Belfast, about 10 minutes from the city centre. From Limavady the usual route is the A6 towards Belfast, then the M2 into the city and south to the Ormeau Road. It is a long drive in each direction, so <strong>online sessions are strongly recommended</strong> for Limavady residents as the most practical option.
                  </p>
                  <p>
                    <strong>By public transport:</strong> If you travel by rail, Botanic and City Hospital are the closest stations to the clinic, with Lanyon Place serving regional routes. Metro bus services also run along the Ormeau Road corridor into south Belfast.
                  </p>
                  <p>
                    <strong>Clinic days and times:</strong> These vary week to week. Current availability and online booking are on the{' '}
                    <a
                      href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 font-medium underline"
                    >
                      clinic booking page
                    </a>
                    . Early evening appointments are available.
                  </p>
                  <p><strong>Hybrid approach:</strong> Some clients choose an initial in-person consultation then switch to online for convenience. Discuss your preferences during booking.</p>
                </div>
              </div>
            </div>

            <div className="bg-accent-50 rounded-xl p-6 border-l-4 border-accent-600">
              <p className="text-gray-800">
                <strong>Practical recommendation:</strong> At approximately 60 miles, the journey from Limavady to our Belfast clinic is a significant commitment for regular sessions. Online sessions are strongly recommended for Limavady residents as the most practical and time-efficient option. You receive the same quality of professional care without any travel. If you do wish to attend in person, clinic days and times vary week to week, and early evening appointments are available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Questions from Limavady Clients
            </h2>
            <div className="space-y-6">

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Should I choose online or in-person sessions from Limavady?
                </h3>
                <p className="text-gray-700">
                  Both options are equally effective therapeutically. However, at approximately 60 miles from Limavady, the Belfast clinic (Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ) is a significant journey for regular sessions. Online sessions are strongly recommended for Limavady residents as the most practical option - eliminating over 2 hours of round-trip travel. If you do prefer face-to-face, clinic days and times vary week to week, and early evening appointments are available. Some clients choose an initial in-person consultation then switch to online for ongoing sessions. Discuss preferences during booking.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you work with business owners and understand entrepreneurial stress?
                </h3>
                <p className="text-gray-700">
                  Yes. Business ownership and entrepreneurship bring specific stressors - financial uncertainty, responsibility for employees, difficulty separating work and personal life, perfectionism, isolation, scaling pressures. I work with several business owners in retail, food production, hospitality, and professional services. Hypnotherapy helps manage chronic business anxiety, improve sleep despite work worries, build resilience for ups and downs, reduce perfectionism, establish work-life boundaries, and process imposter syndrome. Online sessions are particularly practical for entrepreneurs - schedule around your business without leaving for extended periods.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with food industry stress and high-pressure work?
                </h3>
                <p className="text-gray-700">
                  Yes. The food industry brings unique pressures - long hours, physical demands, high-stakes food safety responsibilities, seasonal production stress, quality maintenance challenges. Hypnotherapy helps manage anxiety while maintaining appropriate attention to safety (enhancing rather than impairing performance), improve sleep after long shifts, reduce perfectionism paralysis, build resilience for production pressures, and establish mental boundaries between work stress and home life. The industry won't become less demanding, but your nervous system's response can become more regulated and sustainable.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online sessions as effective as in-person hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  Yes. Clinical research shows no significant difference in outcomes between online and in-person hypnotherapy. Hypnosis works through verbal guidance, visualization, and accessing your subconscious mind - none of which require physical proximity. The therapeutic relationship is built through voice, trust, and your engagement with the process. Many clients prefer online because they're more relaxed in familiar environments and appreciate the time efficiency for maintaining consistent appointments. Professional CNHC-registered therapy, delivered conveniently without compromise.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How does hypnotherapy help with work-life balance and burnout?
                </h3>
                <p className="text-gray-700">
                  Burnout results from sustained work-life imbalance - chronic exhaustion, cynicism, reduced performance, physical symptoms. Hypnotherapy addresses both immediate symptoms (stress reduction, sleep optimization, nervous system regulation) and deeper patterns (identifying values versus inherited expectations, processing guilt around self-care, building self-worth not dependent on productivity, creating sustainable rhythms). For Limavady's driven professionals, this isn't necessarily about working less - it's about working in ways that are genuinely sustainable rather than slowly destructive.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What do I need for online sessions?
                </h3>
                <p className="text-gray-700">
                  You need: (1) Private space where you won't be interrupted (home office, bedroom, even parked car works if needed), (2) Reliable internet connection for video call, (3) Device with camera and microphone (smartphone, tablet, laptop, or desktop all work), (4) Headphones/earbuds recommended for better audio quality. Sessions conducted via secure video platform (Zoom or similar) - simple to use even if you're not tech-savvy. I'll provide setup guidance before your first appointment.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies by individual and concern. Many clients notice improvement within 4-6 sessions. Some issues (specific phobias, smoking cessation, performance anxiety) may respond in 1-3 sessions. Deeper patterns (chronic business anxiety, burnout, complex work-life balance issues, long-term stress) may benefit from 8-12 sessions. We'll discuss realistic expectations during your free initial consultation and create a plan that fits your needs, timeline, and budget. No pressure for long-term commitments you're not comfortable with.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with anxiety and sleep problems?
                </h3>
                <p className="text-gray-700">
                  Yes - anxiety and sleep problems often go together. Racing thoughts keeping you awake, waking at 3-4am with work worry, or sleeping poorly despite being exhausted are common patterns. Hypnotherapy addresses these at the subconscious level where automatic patterns are stored. Techniques include sleep-specific hypnosis, progressive relaxation, cognitive reframing, nervous system regulation, and subconscious suggestion. Research supports hypnotherapy for anxiety and sleep disorders. Many clients report improved sleep quality within 3-5 sessions, which improves their ability to cope with daytime stress.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are you qualified? How do I verify CNHC registration?
                </h3>
                <p className="text-gray-700">
                  Yes - I'm registered with the CNHC (Complementary and Natural Healthcare Council), the UK regulatory body for hypnotherapists accredited by the Professional Standards Authority. This requires 450+ hours of training, professional membership, insurance, and adherence to strict ethical standards. You can verify my registration at www.cnhc.org.uk by searching for my name. I also have 15 years of nursing experience at Belfast City Hospital, bringing healthcare professionalism and clinical understanding to therapy work.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What happens in a hypnotherapy session?
                </h3>
                <p className="text-gray-700">
                  Sessions begin with discussion about your concerns and goals (what brings you, what you want to change). Then guided relaxation - I help you into a calm, focused state through verbal guidance (you remain fully aware and in control, just deeply relaxed). In this state, we work with your subconscious mind using visualization, suggestion, and reframing techniques specific to your issue. You emerge feeling calm and refreshed. Each session tailored to your needs, not a generic script. Online sessions follow the exact same process as in-person, just via video.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What Clients from North West Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Running an independent retail business in Limavady, the stress was constant - finances, staff, competition, everything. Sharon's online hypnotherapy has been transformative. The business anxiety is manageable now, I'm sleeping properly, and I can actually switch off outside work hours. Online sessions were perfect because I could schedule them during quieter trading periods without leaving the shop for travel. Highly recommend for any business owners struggling."
                </p>
                <p className="text-gray-900 font-semibold">— T.M., North West</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Working in food production meant long hours, high pressure, and constant worry about getting everything right. The anxiety was affecting my sleep and health. Sharon understood the specific pressures of the food industry and helped me manage the stress without losing my edge. I can still maintain high standards but without the paralysing anxiety. Online sessions fit perfectly around shift patterns. Brilliant service for anyone in high-pressure work."
                </p>
                <p className="text-gray-900 font-semibold">— L.D., County Londonderry</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The work-life balance was non-existent - I was constantly working, constantly stressed, heading for burnout. Hypnotherapy with Sharon helped me establish actual boundaries and reduce the guilt around taking time for myself. The anxiety has reduced significantly, and I'm functioning better both at work and home. Online format was ideal because I could fit sessions into lunch breaks without the added stress of travel time. Professional and effective."
                </p>
                <p className="text-gray-900 font-semibold">— K.H., North West</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I quit smoking after 18 years with Sharon's help - just 3 online sessions. The hypnotherapy approach worked where everything else had failed. Being able to do it from home meant no excuses, no travel stress, just consistent appointments that actually happened. The money I'm saving on cigarettes has already paid for the therapy many times over. If you're in Limavady putting off quitting because of time or convenience, try online sessions."
                </p>
                <p className="text-gray-900 font-semibold">— P.G., County Londonderry</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The generalized anxiety I'd struggled with for years has reduced dramatically after working with Sharon online. Sleep is better, physical tension has eased, and I can actually relax now. I was skeptical about online therapy at first, but it's genuinely just as effective as face-to-face - maybe better because I'm in my own comfortable space. Sharon understood the pressures of life in a market town and provided practical, effective support. Highly professional service."
                </p>
                <p className="text-gray-900 font-semibold">— A.S., North West</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  );
};

export default LimavadyHypnotherapy;
